import { ChangeDetectionStrategy, Component, NgModule, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import {
  MatTableModule,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow
} from '@angular/material/table';
import { Router, RouterModule, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BaseReportTableComponent } from '@src/app/features/reports/components/base-report-table/base-report-table.component';
import { PatientLinkComponentModule } from '@src/app/features/reports/components/patient-link/patient-link.component';
import { ReportUtilsService } from '@src/app/features/reports/components/report-filters/services/report-utils.service';
import {
  SpeechTreatmentReportAvailableColumns,
  SpeechTreatmentReportHeaders,
  SpeechTreatmentReportViewColumns,
  SpeechTreatmentsReportRecord
} from '../../speech-treatments-report.model';
import { TableStructure } from '@shared/components/table/models/table-structure.model';
import { treatmentPaths } from '@utils/treatment.utils';
import { TreatmentType } from '@shared/components/treatment/treatment.enum';
import { SimpleReportCellComponentModule } from '@src/app/features/reports/components/simple-report-cell/simple-report-cell.component';
import { GeneralCommonsModule } from '@shared/modules/general-commons/general-commons.module';
import { speechTreatmentReportRoute } from '@utils/routing.utils';
import { ReportType } from '@src/app/features/reports/components/report-filters/models/report-filters.model';
import { SimpleReportCellComponent } from '../../../../components/simple-report-cell/simple-report-cell.component';
import { PatientLinkComponent } from '../../../../components/patient-link/patient-link.component';
import { TreatmentStatusIndicatorComponent } from '@shared/components/treatment-status-indicator/treatment-status-indicator.component';
import { TreatmentDateComponent } from '../../../../components/treatment-date/treatment-date.component';
import { ValueSeparatorComponent } from '@shared/modules/general-commons/components/value-separator/value-separator.component';
import { EmptyTableStateComponent } from '@shared/components/empty-table-state/empty-table-state.component';
import { LocalNamePipe } from '@shared/pipes/local-name.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'stx-speech-treatments-report-table',
  templateUrl: './speech-treatments-report-table.component.html',
  imports: [
    MatPaginator,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    SimpleReportCellComponent,
    PatientLinkComponent,
    TreatmentStatusIndicatorComponent,
    RouterLink,
    TreatmentDateComponent,
    ValueSeparatorComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    EmptyTableStateComponent,
    TranslateModule,
    LocalNamePipe
  ]
})
export class SpeechTreatmentsReportTableComponent
  extends BaseReportTableComponent<SpeechTreatmentsReportRecord>
  implements OnInit, OnDestroy
{
  private reportUtilsService = inject(ReportUtilsService);
  private router = inject(Router);
  override readonly reportType: ReportType = this.resolveReportType();

  columns: SpeechTreatmentReportViewColumns = {};
  columnsList: SpeechTreatmentReportAvailableColumns[] = [];
  headers: Partial<SpeechTreatmentReportHeaders> = {};
  records: SpeechTreatmentsReportRecord[] = [];

  readonly speechTreatmentBaseUrl = `${treatmentPaths.get(TreatmentType.SPEECH_TREATMENT).path}/`;
  readonly speechAssessmentBaseUrl = `${treatmentPaths.get(TreatmentType.SPEECH_ASSESSMENT).path}/`;

  constructor() {
    super();
  }

  private resolveReportType(): ReportType {
    return this.router.url.includes(speechTreatmentReportRoute) ? 'SPEECH_TREATMENTS' : 'SPEECH_ASSESSMENTS';
  }

  ngOnInit(): void {
    super.onInit();

    this.watchTableStructure();
  }

  ngOnDestroy(): void {
    super.onDestroy();
  }

  isReevaluateIn(recommendation: string): boolean {
    return recommendation.endsWith('recommend_reevaluate_in') || recommendation.endsWith('recommend_reeval');
  }

  private watchTableStructure() {
    this.subSink.sink = this.reportTableDataService.tableStructure$.subscribe(
      (tableStructure: TableStructure<SpeechTreatmentsReportRecord>) => {
        this.columnsList = this.reportUtilsService.getColumnsList(tableStructure.labels);
        this.columns = this.reportUtilsService.getColumnsNames(tableStructure.labels);
        this.headers = this.reportUtilsService.getPageHeaders({
          columns: tableStructure.labels
        });
        this.records = tableStructure.data.content || [];
        this.changeDetector.detectChanges();
      }
    );
  }
}

@NgModule({
  imports: [
    MatTableModule,
    MatSortModule,
    TranslateModule,
    CommonModule,
    MatIconModule,
    MatPaginatorModule,
    PatientLinkComponentModule,
    RouterModule,
    SimpleReportCellComponentModule,
    GeneralCommonsModule,
    SpeechTreatmentsReportTableComponent
  ],
  exports: [SpeechTreatmentsReportTableComponent]
})
export class SpeechTreatmentsReportTableComponentModule {}
